import { redirect } from '@tanstack/react-router';

import type { IStaticBlocksData } from '@dcentralexpedientedigital/fe-shared/types/config.interface';

import type { UrlPath } from './generate-all-paths';
import { getLocaleFromStoreOrGeolocation } from './get-locale-from-store-or-geo';
import { isValidLocale } from './language';

export const validateLanguageRedirect =
    (urlPaths: UrlPath[], redirectTo?: string) =>
    async ({
        location,
        params,
        context,
    }: {
        location: { pathname: string; search: Record<string, unknown> };
        params: { lang?: string };
        context: {
            getRoute: (pathname: string, search: Record<string, unknown>) => IStaticBlocksData;
        };
    }) => {
        const { pathname } = location;
        const requestedLocale = params.lang;

        if (redirectTo) {
            const locale = getLocaleFromStoreOrGeolocation();
            throw redirect({
                to: `/${locale}${redirectTo}`,
                search: location.search,
            });
        }

        if (!isValidLocale(requestedLocale)) {
            const locale = getLocaleFromStoreOrGeolocation();
            throw redirect({
                to: `/${locale}${pathname.replace(`/${params.lang}`, '')}`,
            });
        }

        const normalizedPath = pathname.replace(`/${params.lang}`, '') || '/';
        const validPathForLocale = urlPaths.find((path) =>
            path.languages.includes(requestedLocale),
        );

        if (!validPathForLocale) {
            //todo: 404 page route should be retrieved from config aswell I guess
            throw redirect({ to: `/${requestedLocale}/404` });
        }

        const expectedPath = Object.keys(params).reduce((acc, param) => {
            if (param !== 'lang') {
                return acc.replace(`$${param}`, params[param as keyof typeof params] || '');
            }
            return acc;
        }, validPathForLocale.path);

        if (expectedPath !== normalizedPath) {
            throw redirect({
                to: `/${requestedLocale}${expectedPath}`,
            });
        }

        // @TODO: Split into separate functions - validateLanguageRedirect for locale validation
        // and validateModalRedirect for modal route handling, called in sequence on beforeLoad.
        const route = context.getRoute(pathname, location.search);
        if (route?.openInModal) {
            throw redirect({
                to: `/${requestedLocale}`,
                search: {
                    ...location.search,
                    modal: normalizedPath.replace('/', ''),
                },
            });
        }
    };
